import { type JSX, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { useQuery } from "urql";
import Loader from "../../shared/components/Loader";
import Modal from "../components/Modal";
import ShowError from "../components/ShowError";
import { type Dispatch, openModal } from "../state";
import Message from "./Message";
import MessagesActions from "./MessagesActions";
import { type IMessage, query } from "./query";
import SelectMessages from "./SelectMessages";
import Send from "./Send";

interface IProps {
    filter: "messages" | "notifications" | "archived";
}

const SelectionBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
`;

function Inbox({ filter }: IProps): JSX.Element {
    const [open, setOpen] = useState<string>("");
    const [replyMessage, setReplyMessage] = useState<IMessage | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const dispatch = useDispatch<Dispatch>();
    const [result] = useQuery({
        query,
        variables: { filter },
    });

    const toggleOpen = useCallback((id: string) => {
        setOpen((current) => (current === id ? "" : id));
    }, []);

    const openReply = useCallback((message: IMessage) => {
        setReplyMessage(message);
        dispatch(openModal("Messages/Reply"));
    }, []);

    const toggleSelected = useCallback((id: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const setSelection = useCallback((ids: string[]) => {
        setSelectedIds(new Set(ids));
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    if (result.fetching) {
        return <Loader />;
    }
    if (result.error) {
        return <ShowError />;
    }

    const messages: IMessage[] = result.data.messages;
    const selectedMessages = messages.filter((message) => selectedIds.has(message.id));

    return (
        <section>
            <SelectionBar>
                <SelectMessages messages={messages} selectedIds={selectedIds} onChangeSelection={setSelection} />
                {selectedMessages.length > 0 && (
                    <MessagesActions
                        messages={selectedMessages}
                        onDone={() => {
                            clearSelection();
                        }}
                    />
                )}
            </SelectionBar>
            {messages.map((message) => (
                <Message
                    key={message.id}
                    message={message}
                    open={open === message.id}
                    toggleOpen={toggleOpen}
                    openReply={openReply}
                    selected={selectedIds.has(message.id)}
                    onToggleSelected={toggleSelected}
                />
            ))}
            <Modal modal="Messages/Reply">
                <Send message={replyMessage} />
            </Modal>
        </section>
    );
}

export default Inbox;
